import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { emailScheme, stringScheme } from 'src/utils/validation-schemes';
import { useUpdateAccountsMutation } from 'src/services/accountsService';
import { useNotification } from 'src/hooks/useNotification';
import { accountPhoneScheme } from '../validation';

interface Account {
  company_name: string;
  email: string;
  phone_number?: string;
}

interface CompanyFormValues {
  company_name: string;
  company_email: string;
  account_phone_number: string;
}

const schema = object({
  company_name: stringScheme({ required: true, min: 3, minMessage: 'Min. 3 characters' }).label(
    'Company Name',
  ),
  company_email: emailScheme({ required: true }).label('Company Email'),
  account_phone_number: accountPhoneScheme().label('Phone Number'),
});

// Owns the "Company" form: default values sourced from the account, dirty-field
// diffing on submit, and the save/cancel side effects.
export const useCompanyInfoForm = (
  account: Account | undefined,
  onDone: () => void,
  onLoadingChange?: (isLoading: boolean) => void,
) => {
  const notification = useNotification();
  const [updateAccountInfo, { isLoading, isSuccess }] = useUpdateAccountsMutation();

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading]);

  const {
    control,
    trigger,
    handleSubmit,
    reset,
    formState: { dirtyFields },
  } = useForm<any>({
    defaultValues: { company_name: '', company_email: '', account_phone_number: '' },
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (account) {
      reset({
        company_name: account.company_name,
        company_email: account.email,
        account_phone_number: account.phone_number || '',
      });
    }
  }, [account, reset]);

  useEffect(() => {
    if (isSuccess) {
      notification.success('Company information has been updated successfully.');
    }
  }, [isSuccess]);

  const submit = handleSubmit((values) => {
    const request: Partial<CompanyFormValues> = (
      Object.keys(dirtyFields) as Array<keyof CompanyFormValues>
    ).reduce((acc, key) => {
      if (dirtyFields[key]) {
        acc[key] = values[key];
      }
      return acc;
    }, {} as Partial<CompanyFormValues>);

    updateAccountInfo(request);
    onDone();
  });

  const cancel = () => {
    reset();
    onDone();
  };

  return { control, trigger, submit, cancel, isLoading };
};
